import convertPropNames from 'src/infrastructure/caseConverter'
import { formatDateIso } from 'src/utils/formatDate'
import http, { Response } from '../../infrastructure/http'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import { Movement, Product } from '../types'
import {
  Barcode,
  EditBarcode,
  FlvProductInfoForReleaseData,
  FlvProductUpdateAvailabilityPayload,
  GenericApiErrors,
  GetFlvProductInfoForReleasePayload,
  GetProduct,
  InsertBarcodeParams,
  MovementByType,
  Reserve,
  UpOrDownOrderBarcode,
} from './StockManagementTypes'

interface FCandModelId {
  fulfillmentCenterId: number,
  modelId: number,
}
interface GetProductReserves extends FCandModelId {
  startDate?: Date,
  endDate?: Date,
}

interface GetMovementsGroupByType extends FCandModelId {
  startDate: Date,
  endDate: Date,
}

const getProduct = async ({
  modelId,
  fulfillmentCenterId,
}: GetProduct) => {
  const result = await http.get('v2/stock/product', {
    params: {
      model_id: modelId,
      fulfillment_center_id: fulfillmentCenterId,
    },
  })

  return convertPropNames<Response<Product>>(result.data)
}

interface GetProductBarcodes {
  modelId: number
}

const getProductBarcodes = async ({
  modelId,
}: GetProductBarcodes) => {
  const result = await http.get(`v2/product/${modelId}/barcodes`)

  return convertPropNames<Response<Movement[]>>(result.data)
}

interface SendAdjustmentFile {
  file: File
}

const postAdjustmentFile = async ({
  file,
}: SendAdjustmentFile) => {
  const formData = new FormData()
  formData.append('selectedFile', file)

  await http.post(
    '/v2/stock/adjustment-by-excel',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}

const getProductReserves = async ({
  fulfillmentCenterId,
  modelId,
  startDate,
  endDate,
}: GetProductReserves) => {
  const result = await http.get('v2/stock/reserves', {
    params: {
      fulfillment_center_id: fulfillmentCenterId,
      model_id: modelId,
      start_date: formatDateIso(startDate),
      end_date: formatDateIso(endDate),
    },
  })

  return convertPropNames<Response<Reserve[]>>(result.data)
}

const getProductMovementsByType = async ({
  fulfillmentCenterId,
  modelId,
  startDate,
  endDate,
}: GetMovementsGroupByType) => {
  const result = await http.get('v2/stock/movements-group-by-type-and-date', {
    params: {
      fulfillment_center_id: fulfillmentCenterId,
      model_id: modelId,
      start_date: formatDateIso(startDate),
      end_date: formatDateIso(endDate),
    },
  })

  return convertPropNames<Response<MovementByType[]>>(result.data)
}

const deleteBarcode = async (payload: EditBarcode) => {
  try {
    const { barcode, modelId, badge } = payload

    const body = {
      headers: {
        'user-barcode': badge,
        'content-type': 'application/json',
      },
      data: { barcode, id_model: modelId },
    }

    const result = await http.delete('v2/stock/barcode', body)

    return parseGoodResponse<Barcode[]>(result.data.barcodes)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const insertNewBarcode = async (payload: InsertBarcodeParams) => {
  try {
    const {
      modelId, barcode, packQty,
    } = payload

    const result = await http.post('v2/stock/barcode', {
      id_model: modelId,
      barcode,
      pack_qty: packQty,
    })
    return parseGoodResponse<number>(result.data.order)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const upBarcodeOrder = async (payload: UpOrDownOrderBarcode) => {
  try {
    const {
      barcode, type, modelId, order,
    } = payload

    const body = {
      barcode,
      type,
      id_model: modelId,
      order,
    }

    const result = await http.put('v2/stock/barcode/order', body)

    return parseGoodResponse<Barcode[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const downBarcodeOrder = async (payload: UpOrDownOrderBarcode) => {
  try {
    const {
      barcode, type, modelId, order,
    } = payload

    const body = {
      barcode,
      type,
      id_model: modelId,
      order,
    }

    const result = await http.put('v2/stock/barcode/order', body)

    return parseGoodResponse<Barcode[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getFlvProductInfoForRelease = async (payload: GetFlvProductInfoForReleasePayload) => {
  try {
    const {
      fulfillmentCenterId, selectedDayPeriod, productsCodes, productDescription,
    } = payload

    const body = {
      day_period: selectedDayPeriod,
      products_codes: productsCodes,
      product_description: productDescription,
    }

    if (fulfillmentCenterId !== undefined && fulfillmentCenterId > 0) {
      Object.assign(body, { id_fulfillment_center: fulfillmentCenterId })
    }

    const result = await http.post('/v2/same-day-delivery/flv-release-info', body)
    return parseGoodResponse<FlvProductInfoForReleaseData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const resendUpdateForFlvRelease = async (payload: FlvProductUpdateAvailabilityPayload) => {
  try {
    const products = payload.products.map((product) => ({
      ids_model: product.idsModel,
      id_fulfillment_center: product.idFulfillmentCenter,
    }))

    const body = {
      products,
    }

    await http.post('/v2/same-day-delivery/update-flv', body, {
      params: {
        day_period: payload.dayPeriod,
      },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getProduct,
  getProductBarcodes,
  postAdjustmentFile,
  getProductReserves,
  getProductMovementsByType,
  deleteBarcode,
  insertNewBarcode,
  upBarcodeOrder,
  downBarcodeOrder,
  getFlvProductInfoForRelease,
  resendUpdateForFlvRelease,
}
